import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import patientService from '../../../../../services/patient.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../../utils/ecxSwal';
import { PatientCommPrefsSkeleton } from '../subparts/PatientTabSkeletons';

const PRIORITY_OPTIONS = [
    { value: 1, label: 'Primary' },
    { value: 2, label: 'Secondary' },
    { value: 3, label: 'Tertiary' },
];

const CHANNELS = [
    { toggle: 'isEmail', pref: 'prefEmail', label: 'Email', icon: 'bi-envelope' },
    { toggle: 'isSms', pref: 'prefSms', label: 'SMS', icon: 'bi-chat-dots' },
    { toggle: 'isCall', pref: 'prefCall', label: 'Phone Call', icon: 'bi-telephone' },
];

const NOTIFICATIONS = [
    { name: 'isReminder', label: 'Reminders', icon: 'bi-bell' },
    { name: 'isNewsletter', label: 'Newsletter', icon: 'bi-newspaper' },
    { name: 'isArticle', label: 'Articles', icon: 'bi-file-text' },
    { name: 'isRecall', label: 'Recall', icon: 'bi-arrow-repeat' },
    { name: 'isGlass', label: 'Glasses', icon: 'bi-eyeglasses' },
    { name: 'isLens', label: 'Lenses', icon: 'bi-eye' },
    { name: 'isLensOrder', label: 'Lens Orders', icon: 'bi-bag-check' },
    { name: 'isSurvey', label: 'Survey', icon: 'bi-clipboard-data' },
    { name: 'isThankyou', label: 'Thank You', icon: 'bi-heart' },
    { name: 'isFollowup', label: 'Follow-up', icon: 'bi-arrow-return-right' },
    { name: 'isBirthday', label: 'Birthday', icon: 'bi-cake2' },
];

const getPriorityLabel = (value) =>
    PRIORITY_OPTIONS.find((option) => option.value === Number(value))?.label || '—';

const NOTIFICATION_FIELDS = NOTIFICATIONS.map((item) => item.name);
const CHANNEL_TOGGLE_FIELDS = CHANNELS.map((channel) => channel.toggle);
const CHANNEL_PREF_FIELDS = CHANNELS.map((channel) => channel.pref);
const CHANNEL_FIELDS = [...CHANNEL_TOGGLE_FIELDS, ...CHANNEL_PREF_FIELDS];

const toBoolean = (value, fallback = false) => {
    if (value === undefined || value === null) {
        return Boolean(fallback);
    }

    return value === true || value === 'true' || value === 'on' || value === 1 || value === '1';
};

const buildCommPrefsPayload = (settings, formValues, editingSection) => {
    const readBoolean = (field, section) => {
        const raw = formValues[field];

        if (raw !== undefined && raw !== null) {
            return toBoolean(raw);
        }

        if (editingSection === section) {
            return false;
        }

        return Boolean(settings[field]);
    };

    return {
        id: settings.id,
        patientId: settings.patientId,
        isEmail: readBoolean('isEmail', 'channels'),
        isSms: readBoolean('isSms', 'channels'),
        isCall: readBoolean('isCall', 'channels'),
        prefEmail: Number(formValues.prefEmail ?? settings.prefEmail),
        prefSms: Number(formValues.prefSms ?? settings.prefSms),
        prefCall: Number(formValues.prefCall ?? settings.prefCall),
        isReminder: readBoolean('isReminder', 'notifications'),
        isNewsletter: readBoolean('isNewsletter', 'notifications'),
        isArticle: readBoolean('isArticle', 'notifications'),
        isRecall: readBoolean('isRecall', 'notifications'),
        isGlass: readBoolean('isGlass', 'notifications'),
        isLens: readBoolean('isLens', 'notifications'),
        isLensOrder: readBoolean('isLensOrder', 'notifications'),
        isSurvey: readBoolean('isSurvey', 'notifications'),
        isThankyou: readBoolean('isThankyou', 'notifications'),
        isFollowup: readBoolean('isFollowup', 'notifications'),
        isBirthday: readBoolean('isBirthday', 'notifications'),
    };
};

function PreferenceSection({ title, icon, children, actions }) {
    return (
        <section className="patient-pref-section">
            <div className="patient-pref-section__header">
                <div className="patient-pref-section__header-main">
                    <span className="patient-pref-section__icon">
                        <i className={`bi ${icon}`} />
                    </span>
                    <h4 className="patient-pref-section__title">{title}</h4>
                </div>
                {actions ? <div className="patient-pref-section__actions">{actions}</div> : null}
            </div>
            <div className="patient-pref-section__body">{children}</div>
        </section>
    );
}

function ChannelCard({ channel, isEditing, register, settings, formValues }) {
    const enabled = isEditing
        ? (formValues[channel.toggle] ?? settings[channel.toggle])
        : settings[channel.toggle];
    const priority = isEditing
        ? (formValues[channel.pref] ?? settings[channel.pref])
        : settings[channel.pref];

    return (
        <div className={`patient-pref-channel ${enabled ? 'is-enabled' : ''}`}>
            <div className="patient-pref-channel__top">
                <div className="patient-pref-channel__identity">
                    <span className="patient-pref-channel__icon">
                        <i className={`bi ${channel.icon}`} />
                    </span>
                    <div>
                        <div className="patient-pref-channel__label">{channel.label}</div>
                        <div className="patient-pref-channel__status">
                            {enabled ? 'Channel enabled' : 'Channel disabled'}
                        </div>
                    </div>
                </div>

                {isEditing ? (
                    <div className="form-check form-switch m-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`channel-${channel.toggle}`}
                            {...register(channel.toggle)}
                        />
                    </div>
                ) : (
                    <span className={`patient-pref-status-pill ${enabled ? 'is-on' : 'is-off'}`}>
                        <i className={`bi ${enabled ? 'bi-check-circle' : 'bi-x-circle'}`} />
                        {enabled ? 'Enabled' : 'Disabled'}
                    </span>
                )}
            </div>

            <div className="patient-pref-channel__priority">
                <span className="patient-pref-channel__priority-label">Priority</span>
                {isEditing ? (
                    <select className="form-select form-select-sm" {...register(channel.pref)}>
                        {PRIORITY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span className="patient-pref-priority-pill">{getPriorityLabel(priority)}</span>
                )}
            </div>
        </div>
    );
}

function NotificationTile({ item, isEditing, register, settings, formValues }) {
    const enabled = isEditing
        ? (formValues[item.name] ?? settings[item.name])
        : settings[item.name];

    return (
        <div className={`patient-pref-notification ${enabled ? 'is-enabled' : ''}`}>
            <div className="patient-pref-notification__main">
                <span className="patient-pref-notification__icon">
                    <i className={`bi ${item.icon}`} />
                </span>
                <span className="patient-pref-notification__label">{item.label}</span>
            </div>

            {isEditing ? (
                <div className="form-check form-switch m-0">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id={`notification-${item.name}`}
                        {...register(item.name)}
                    />
                </div>
            ) : (
                <span className={`patient-pref-status-pill ${enabled ? 'is-on' : 'is-off'}`}>
                    <i className={`bi ${enabled ? 'bi-check-circle' : 'bi-x-circle'}`} />
                    {enabled ? 'On' : 'Off'}
                </span>
            )}
        </div>
    );
}

const PatientCommunicationPreferenceCard = ({ patient, onCommPrefsUpdated }) => {
    const [editingSection, setEditingSection] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const patientId = patient?.id;

    const {
        register,
        reset,
        watch,
        getValues,
        setValue,
        formState: { dirtyFields },
    } = useForm();

    const formValues = watch();

    useEffect(() => {
        if (patientId) {
            fetchPreferences();
        }
    }, [patientId]);

    const fetchPreferences = async () => {
        if (!patientId) {
            return;
        }

        try {
            setLoading(true);
            const res = await patientService.getCommunicationPreferences(clinicId, patientId);
            setSettings(res.data);
            reset(res.data);
            onCommPrefsUpdated?.(res.data);
        } catch (e) {
            console.error(e);
            showError({ title: 'Load Failed', text: 'Failed to load communication preferences.' });
        } finally {
            setLoading(false);
        }
    };

    const saveCommPrefs = async () => {
        try {
            setSaving(true);
            const payload = buildCommPrefsPayload(settings, getValues(), editingSection);
            const res = await patientService.updateCommunicationPreferences(clinicId, patientId, payload);
            setSettings(res.data);
            reset(res.data);
            onCommPrefsUpdated?.(res.data);
            setEditingSection(null);
            showSuccess({
                title: 'Preferences Updated',
                text: 'Communication preferences were saved successfully.',
            });
        } catch (e) {
            console.error(e);
            showError({ title: 'Update Failed', text: 'Failed to update communication preferences.' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        reset(settings);
        setEditingSection(null);
    };

    const setBulkToggleValues = (fields, enabled) => {
        fields.forEach((field) => {
            setValue(field, enabled, { shouldDirty: true });
        });
    };

    const handleEnableAll = (section) => {
        if (section === 'channels') {
            setBulkToggleValues(CHANNEL_TOGGLE_FIELDS, true);
            return;
        }

        setBulkToggleValues(NOTIFICATION_FIELDS, true);
    };

    const handleDisableAll = (section) => {
        if (section === 'channels') {
            setBulkToggleValues(CHANNEL_TOGGLE_FIELDS, false);
            return;
        }

        setBulkToggleValues(NOTIFICATION_FIELDS, false);
    };

    const renderBulkActions = (section) => {
        if (editingSection !== section) {
            return null;
        }

        return (
            <div className="patient-pref-bulk-actions">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={() => handleEnableAll(section)}
                    disabled={saving}
                >
                    <i className="bi bi-check2-all me-1" />
                    Enable All
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleDisableAll(section)}
                    disabled={saving}
                >
                    <i className="bi bi-x-lg me-1" />
                    Disable All
                </button>
            </div>
        );
    };

    const isChannelsDirty = CHANNEL_FIELDS.some((field) => dirtyFields[field]);
    const isNotificationsDirty = NOTIFICATION_FIELDS.some((field) => dirtyFields[field]);

    const renderSectionActions = (section, isDirty) => {
        const isEditing = editingSection === section;

        if (!isEditing) {
            return (
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm"
                    onClick={() => setEditingSection(section)}
                    disabled={Boolean(editingSection) || saving}
                >
                    <i className="bi bi-pencil me-1" />
                    Edit
                </button>
            );
        }

        return (
            <div className="d-flex gap-2">
                <button
                    type="button"
                    className="ecx-modal__close-btn btn btn-sm btn-outline-secondary px-3"
                    onClick={handleCancelEdit}
                    disabled={saving}
                >
                    <i className="bi bi-x-lg me-1" />
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-accent"
                    onClick={saveCommPrefs}
                    disabled={!isDirty || saving}
                >
                    <i className={`bi ${saving ? 'bi-arrow-repeat' : 'bi-check2'} me-1`} />
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        );
    };

    if (loading || !settings) {
        return <PatientCommPrefsSkeleton />;
    }

    return (
        <div className="patient-detail-tab-panel patient-pref-panel">
            <div className="patient-detail-tab-panel__header">
                <div>
                    <h3 className="patient-detail-tab-panel__title">
                        <i className="bi bi-chat-dots-fill" />
                        Communication Preferences
                    </h3>
                    <p className="patient-detail-tab-panel__subtitle">
                        Manage channels and notification types for this patient.
                    </p>
                </div>
            </div>

            {settings && (
                <div className="patient-pref-layout">
                    <PreferenceSection
                        title="Communication Channels"
                        icon="bi-telephone-fill"
                        actions={renderSectionActions('channels', isChannelsDirty)}
                    >
                        {renderBulkActions('channels')}
                        <div className="patient-pref-channel-grid">
                            {CHANNELS.map((channel) => (
                                <ChannelCard
                                    key={channel.toggle}
                                    channel={channel}
                                    isEditing={editingSection === 'channels'}
                                    register={register}
                                    settings={settings}
                                    formValues={formValues}
                                />
                            ))}
                        </div>
                    </PreferenceSection>

                    <PreferenceSection
                        title="Notifications & Content"
                        icon="bi-bell-fill"
                        actions={renderSectionActions('notifications', isNotificationsDirty)}
                    >
                        {renderBulkActions('notifications')}
                        <div className="patient-pref-notification-grid">
                            {NOTIFICATIONS.map((item) => (
                                <NotificationTile
                                    key={item.name}
                                    item={item}
                                    isEditing={editingSection === 'notifications'}
                                    register={register}
                                    settings={settings}
                                    formValues={formValues}
                                />
                            ))}
                        </div>
                    </PreferenceSection>
                </div>
            )}
        </div>
    );
};

export default PatientCommunicationPreferenceCard;
